import React from 'react';
import { Box, Typography, Button, Chip, IconButton } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IMAGE_URL } from '../../services/api';
import ProtectedImage from '../../components/ProtectedImage';
import FormDrawer from '../../components/layout/FormDrawer';
import { BRAND, primaryButtonSx } from '../../components/auth/authTheme';

const DRAWER_WIDTH = { xs: "100%", sm: 600, md: 820 };

const DetailItem = ({ icon, label, value, fullWidth = false }) => (
  <Box
    sx={{
      display: "flex",
      gap: 2,
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${BRAND.border}`,
      bgcolor: "#fff",
      gridColumn: fullWidth ? "1 / -1" : undefined,
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        bgcolor: BRAND.orangeLight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: BRAND.orange,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: BRAND.muted,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontSize: "0.7rem",
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: value ? BRAND.text : BRAND.muted,
          fontWeight: value ? 500 : 400,
          mt: 0.25,
          wordBreak: "break-word",
        }}
      >
        {value || "Not provided"}
      </Typography>
    </Box>
  </Box>
);

const DetailGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
      gap: 2,
    }}
  >
    {children}
  </Box>
);

const ProjectViewModal = ({ show, handleClose, selectedProject }) => {
  if (!selectedProject) return null;

  return (
    <FormDrawer
      open={show}
      onClose={handleClose}
      width={DRAWER_WIDTH}
      title={selectedProject.title || "Project Details"}
      subtitle={selectedProject.is_active ? "Status: Active" : "Status: Inactive"}
    >
      <Box sx={{ mb: 3 }}>
        <DetailGrid>
          <DetailItem
            icon={<TitleOutlinedIcon fontSize="small" />}
            label="Title"
            value={selectedProject.title}
          />
          <DetailItem
            icon={<AttachMoneyOutlinedIcon fontSize="small" />}
            label="Budget"
            value={selectedProject.project_budget}
          />
          <DetailItem
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label="Start Date"
            value={selectedProject.start_date}
          />
          <DetailItem
            icon={<CalendarTodayOutlinedIcon fontSize="small" />}
            label="End Date"
            value={selectedProject.end_date}
          />
          <DetailItem
            icon={<DescriptionOutlinedIcon fontSize="small" />}
            label="Description"
            value={selectedProject.description}
            fullWidth
          />
          <DetailItem
            icon={<AssignmentOutlinedIcon fontSize="small" />}
            label="Scope"
            value={selectedProject.scope}
            fullWidth
          />
        </DetailGrid>
      </Box>

      {selectedProject.attachments && selectedProject.attachments.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BRAND.navy, mb: 1.5 }}>
            Attachments
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {selectedProject.attachments.map((url, index) => {
              const fullUrl = `${IMAGE_URL}${url}`;
              const isPDF = fullUrl.toLowerCase().endsWith('.pdf');
              const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fullUrl);
              const fileName = url.split('/').pop() || `Attachment ${index + 1}`;

              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: "12px",
                    width: '100%',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      borderColor: BRAND.navy,
                      bgcolor: BRAND.bg
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '10px',
                      bgcolor: isPDF ? '#ffebee' : isImage ? '#e3f2fd' : '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: isPDF ? '#f44336' : isImage ? '#2196f3' : '#9e9e9e'
                    }}
                  >
                    {isPDF ? <PictureAsPdfIcon /> : isImage ? <ImageIcon /> : <InsertDriveFileIcon />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {fileName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: BRAND.muted }}>
                      {isPDF ? 'PDF Document' : isImage ? 'Image File' : 'Document'}
                    </Typography>
                  </Box>
                  <IconButton 
                    size="small" 
                    onClick={() => window.open(fullUrl, '_blank')}
                    sx={{ color: BRAND.orange, bgcolor: BRAND.orangeLight, '&:hover': { bgcolor: BRAND.orange, color: '#fff' } }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </FormDrawer>
  );
};

export default ProjectViewModal;
